'use client';

import { cn } from '@/lib/utils';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import React, { createContext, useCallback, useEffect, useId, useState } from 'react';
import styles from './slider.module.scss';

export type SliderProps = {
  children?: React.ReactNode;
  autoplay: boolean;
  controls: boolean;
  featuredMove: string;
  border: boolean;
  borderRadius: boolean;
  slideBackground: string;
  shadow: boolean;
};

type SliderContextType = {
  instance: string;
};

export const SliderContext = createContext<SliderContextType | null>(null);

export const Slider = ({
  children,
  autoplay,
  controls,
  featuredMove,
  border,
  borderRadius,
  slideBackground,
  shadow,
}: SliderProps) => {
  const slidesPerView = 1;
  const loop = true;

  const childrenArray = React.Children.toArray(children);
  const originalSlidesCount = childrenArray.length;
  const needsMoreSlides = originalSlidesCount > 0 && originalSlidesCount < 5;

  const renderedSlides = needsMoreSlides ? [...childrenArray, ...childrenArray] : childrenArray;

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop,
      align: 'center',
    },
    [
      ...(autoplay
        ? [
            Autoplay({
              delay: 3000,
              stopOnInteraction: false,
            }),
          ]
        : []),
    ],
  );

  const resetAutoplay = useCallback(() => {
    const autoplayPlugin = emblaApi?.plugins()?.autoplay;
    autoplayPlugin?.reset();
  }, [emblaApi]);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedSnap, setSelectedSnap] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const snap = emblaApi.selectedScrollSnap();
    setSelectedSnap(snap);
    const normalizedIndex = originalSlidesCount > 0 ? snap % originalSlidesCount : snap;
    setSelectedIndex(normalizedIndex);
  }, [emblaApi, originalSlidesCount]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onSelect);
    emblaApi.on('pointerUp', resetAutoplay);
    onSelect();
  }, [emblaApi, onSelect, resetAutoplay]);

  const scrollTo = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
      resetAutoplay();
    },
    [emblaApi, resetAutoplay],
  );

  const scrollPrev = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
    resetAutoplay();
  }, [emblaApi, resetAutoplay]);

  const scrollNext = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
    resetAutoplay();
  }, [emblaApi, resetAutoplay]);

  const instance = useId();

  function sliderControls() {
    if (!controls) return null;

    return (
      <div className={styles.nav}>
        <button
          className={cn('arrow', styles.arrow, styles.arrowLeft, shadow ? styles.shadow : '')}
          onClick={scrollPrev}
          aria-label="Previous slide"
        >
          <span className={styles.arrowIcon} />
        </button>

        <div className={styles.bullets}>
          {Array.from({ length: originalSlidesCount }).map((_, index) => {
            const isActive = selectedIndex === index;

            return (
              <button
                key={index}
                onClick={() => scrollTo(index)}
                className={cn(isActive ? styles.active : '', autoplay ? '' : styles.bPaused)}
                aria-label={`Go to slide ${index + 1}`}
              >
                <div className={cn(styles.bullet, autoplay ? '' : styles.bulletPaused)}></div>
              </button>
            );
          })}
        </div>

        <button
          className={cn('arrow', styles.arrow, styles.arrowRight, shadow ? styles.shadow : '')}
          onClick={scrollNext}
          aria-label="Next slide"
        >
          <span className={styles.arrowIcon} />
        </button>
      </div>
    );
  }

  const parentClasses = cn(
    styles.wrapper,
    border && '_border',
    borderRadius && '_borderRadius',
    slideBackground,
    featuredMove,
    shadow && '_shadow',
  );

  return (
    <SliderContext.Provider value={{ instance }}>
      <div className={cn(styles.container, featuredMove)}>
        <div ref={emblaRef} style={{ overflow: 'hidden' }}>
          <div className={parentClasses}>
            {renderedSlides.map((child, index) => (
              <div
                key={index}
                className={cn(styles.slide, index === selectedSnap && 'slideActive')}
                style={{ flex: `0 0 ${100 / slidesPerView}%` }}
              >
                {child}
              </div>
            ))}
          </div>
        </div>
        {sliderControls()}
      </div>
    </SliderContext.Provider>
  );
};
